<template>
    <Modal :isOpen="isOpen" @close="$emit('close')">
        <template #title>
            <div class="flex items-center gap-3">
                <div
                    class="w-12 h-12 p-3.5 bg-red-50 rounded-full flex items-center justify-center"
                >
                    <Icon
                        icon="trash"
                        class="w-6 h-6 text-red-500"
                        pathClass=""
                    />
                </div>
                <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100">
                    Delete connection
                </h2>
            </div>
        </template>

        <template #content>
            <div class="space-y-6">
                <div class="space-y-3 text-sm text-slate-800 dark:text-slate-100">
                    <p>
                        You are about to delete the connection to
                        {{ connection.name }}. My10x will stop syncing the
                        accounts from this connection. Here's what happens:
                    </p>
                    <ul class="list-disc space-y-1 pl-5">
                        <li>
                            Cards from this connection stay in your wallet as
                            manually added cards. They no longer update
                            automatically.
                        </li>
                        <li>
                            Their benefit history is kept, including credits
                            you've logged, benefit settings, welcome bonus
                            tracking and open dates.
                        </li>
                        <li>
                            Points balances keep their current value and
                            become manual balances that you update yourself.
                            Balance history synced from this connection is
                            removed.
                        </li>
                        <li>
                            Removing a card is a separate step. To remove one,
                            delete it from My Wallet.
                        </li>
                    </ul>
                </div>

                <div class="space-y-2">
                    <label
                        for="confirm-text"
                        class="block text-sm font-medium text-slate-600 dark:text-slate-400"
                    >
                        Please enter "Delete connection" to confirm you would
                        like to remove
                        <span>
                            {{ nickname ? nickname : 'this connection' }}.
                        </span>
                    </label>
                    <input
                        id="confirm-text"
                        type="text"
                        placeholder="Enter confirm text"
                        class="block w-full bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 text-gray-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        v-model="confirmText"
                    />
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end gap-2">
                <button
                    class="px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 text-sm font-medium border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors active:bg-slate-200 focus:outline-slate-200 focus:border-slate-200 hover:border-slate-300 active:border-slate-300"
                    @click="$emit('close')"
                >
                    Cancel
                </button>
                <button
                    class="px-4 py-2.5 bg-red-600 rounded shadow text-sm font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:bg-slate-200 focus:outline-slate-200 focus:border-slate-200 hover:border-slate-300 active:border-slate-300"
                    :disabled="!isConfirmTextValid"
                    @click="handleDelete"
                >
                    Delete
                </button>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import Modal from '@/components/ui/modal/ui-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    connection: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['close', 'delete']);
const confirmText = ref('');

const isConfirmTextValid = computed(() => {
    return confirmText.value.toLowerCase() === 'delete connection';
});

const nickname = computed(() => {
    return props.connection.connect_attempt?.connection_nickname;
});

function handleDelete() {
    if (isConfirmTextValid.value) {
        emit('delete');
        confirmText.value = '';
    }
}
</script>
